import { cloneDeep, findIndex, slice } from 'lodash'

import { LocationTreeNode } from 'containers/Devices/services/locationPicker-service'
import { TREE_LEVELS } from '../../../../services/constants'
import {
  changeChecked,
  changePropertyChecked,
  changePropertyState,
  selectAllByProperties,
} from './checkNode-service'
import { initialState } from './customTree-constants'
import {
  buildPropertyTree,
  changeExpanded,
  changeUserPrefByProperty,
  getAllSelectedState,
  prepareProperties,
} from './customTree-service'
import locationPickerTreeActions from './locationPickerTree-actions'

export type LocationPickerTreeState = {
  nodes: LocationTreeNode[]
  isAllSelected: boolean
  treeCopy: LocationTreeNode | null
  userPrefCurrent: number[] | null
  finalNode: string
}

type LocationPickerTreeAction = {
  type: string
  payload?: {
    treeRoot?: LocationTreeNode
    allowedLocationIds?: number[] | null
    key?: number
    propertyId?: number
    bool?: boolean
    isSelectAll?: boolean
  }
}

export const locationPickerTreeReducer = (
  state: LocationPickerTreeState = initialState as LocationPickerTreeState,
  action: LocationPickerTreeAction
): LocationPickerTreeState => {
  switch (action.type) {
    case locationPickerTreeActions.actionsTypes.LOAD_NODES_SUCCESS: {
      const { treeRoot, allowedLocationIds } = action.payload || {}
      const isAllSelected = allowedLocationIds === null
      const userPrefCurrent = allowedLocationIds || []
      const treeCopy = cloneDeep(treeRoot)

      return {
        ...state,
        treeCopy: treeCopy as LocationTreeNode,
        userPrefCurrent,
        isAllSelected,
        nodes: prepareProperties(
          { nodes: treeRoot },
          userPrefCurrent,
          isAllSelected
        ) as LocationTreeNode[],
        finalNode: TREE_LEVELS.PLACEMENT,
      }
    }
    case locationPickerTreeActions.actionsTypes.SET_EXPAND_NODE: {
      const { key, propertyId } = action.payload || {}
      if (key === undefined) {
        return state
      }
      const propertyIndex = findIndex(state.nodes, ['id', propertyId])
      const property = cloneDeep(state.nodes[propertyIndex])
      changeExpanded(key, property.children || [])

      return {
        ...state,
        nodes: [
          ...slice(state.nodes, 0, propertyIndex),
          property,
          ...slice(state.nodes, propertyIndex + 1),
        ],
      }
    }
    case locationPickerTreeActions.actionsTypes.SET_PROPERTY_EXPAND_NODE: {
      const { key } = action.payload || {}
      const expandedPropertyIndex = findIndex(state.nodes, ['id', key])
      const expandedProperty = buildPropertyTree(state, expandedPropertyIndex)

      return {
        ...state,
        nodes: [
          ...slice(state.nodes, 0, expandedPropertyIndex),
          expandedProperty as LocationTreeNode,
          ...slice(state.nodes, expandedPropertyIndex + 1),
        ],
      }
    }
    case locationPickerTreeActions.actionsTypes.SET_PROPERTY_CHECK_NODE: {
      const { key, bool } = action.payload || {}
      const propertyIndex = findIndex(state.nodes, ['id', key])
      const property = cloneDeep(state.nodes[propertyIndex])
      changePropertyChecked(property, !!bool)
      const newCurrentUserPref = changeUserPrefByProperty(
        property,
        state.userPrefCurrent || []
      )

      return {
        ...state,
        userPrefCurrent: newCurrentUserPref,
        nodes: [
          ...slice(state.nodes, 0, propertyIndex),
          property,
          ...slice(state.nodes, propertyIndex + 1),
        ],
      }
    }
    case locationPickerTreeActions.actionsTypes.SET_CHECK_NODE: {
      const { key, bool, propertyId } = action.payload || {}
      if (key === undefined) {
        return state
      }
      const propertyIndex = findIndex(state.nodes, ['id', propertyId])
      const property = cloneDeep(state.nodes[propertyIndex])

      property.children = changeChecked(
        key,
        !!bool,
        TREE_LEVELS.PROPERTY,
        property.children || []
      )

      changePropertyState(property)

      return {
        ...state,
        nodes: [
          ...slice(state.nodes, 0, propertyIndex),
          property,
          ...slice(state.nodes, propertyIndex + 1),
        ],
      }
    }
    case locationPickerTreeActions.actionsTypes.SET_SELECT_ALL_STATE:
      return {
        ...state,
        isAllSelected: !!getAllSelectedState(state.nodes),
      }
    case locationPickerTreeActions.actionsTypes.SELECT_ALL:
      return {
        ...state,
        nodes: selectAllByProperties(
          state.nodes,
          !!action.payload?.isSelectAll
        ) as LocationTreeNode[],
        isAllSelected: !!action.payload?.isSelectAll,
        userPrefCurrent: action.payload?.isSelectAll ? null : [],
      }
    case locationPickerTreeActions.actionsTypes.RESET_TREE_COPY_STATE:
      return initialState as LocationPickerTreeState
    default:
      return state
  }
}
